import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Checkbox,
  Paper,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { getDashboardEnabledCategories } from '../../utils/dashboardPermissions';

// Definición de todos los gráficos disponibles organizados por categoría
const AVAILABLE_CHARTS = {
  sales: [
    {
      id: 'salesTrendStacked',
      name: 'Tendencia de Ventas',
      component: 'SalesTrendChartStacked',
      defaultSize: { xs: 12, md: 8 },
      category: 'sales'
    },
    {
      id: 'branchSales',
      name: 'Ventas por Sucursal',
      component: 'BranchSalesChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'sales'
    },
    {
      id: 'branchSalesTrend',
      name: 'Tendencia de Ventas por Sucursal',
      component: 'BranchSalesTrendChart',
      defaultSize: { xs: 12, md: 12 },
      category: 'sales'
    },
    {
      id: 'topProducts',
      name: 'Productos Más Vendidos',
      component: 'TopProductsChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'sales'
    },
    {
      id: 'salesByChannel',
      name: 'Ventas por Canal',
      component: 'SalesByChannelChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'sales'
    },
    {
      id: 'paymentMethods',
      name: 'Métodos de Pago',
      component: 'PaymentMethodsChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'sales'
    },
    {
      id: 'salesByHour',
      name: 'Ventas por Hora',
      component: 'SalesByHourChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'sales'
    },
    {
      id: 'categorySales',
      name: 'Ventas por Categoría',
      component: 'CategorySalesChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'sales'
    },
    {
      id: 'salesComparison',
      name: 'Comparación de Ventas',
      component: 'SalesComparisonChart',
      defaultSize: { xs: 12, md: 8 },
      category: 'sales'
    },
    {
      id: 'averageSalesByBranch',
      name: 'Promedio de Ventas por Sucursal',
      component: 'AverageSalesByBranchChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'sales'
    },
    {
      id: 'sellerPerformance',
      name: 'Rendimiento por Vendedor',
      component: 'SellerPerformanceTable',
      defaultSize: { xs: 12, md: 8 },
      category: 'sales'
    },
  ],
  branch: [
    {
      id: 'branchParticipation',
      name: 'Participación por Sucursal',
      component: 'BranchParticipationPieChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'branch'
    },
    {
      id: 'definedVsUndefined',
      name: 'Definido vs Indefinido',
      component: 'DefinedVsUndefinedChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'branch'
    },
    {
      id: 'averageTicketByBranch',
      name: 'Ticket Promedio por Sucursal',
      component: 'AverageTicketByBranchChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'branch'
    },
    {
      id: 'paymentMethodsByBranch',
      name: 'Métodos de Pago por Sucursal',
      component: 'PaymentMethodsByBranchChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'branch'
    },
    {
      id: 'branchMonthlyGrowth',
      name: 'Crecimiento Mensual por Sucursal',
      component: 'BranchMonthlyGrowthChart',
      defaultSize: { xs: 12, md: 8 },
      category: 'branch'
    },
  ],
  inventory: [
    {
      id: 'stockValueByBranch',
      name: 'Valor de Stock por Sucursal',
      component: 'StockValueByBranchChart',
      defaultSize: { xs: 12, md: 4 },
      category: 'inventory'
    },
    {
      id: 'monthlyProduction',
      name: 'Producción Mensual',
      component: 'MonthlyProductionChart',
      defaultSize: { xs: 12, md: 8 },
      category: 'inventory'
    },
  ],
};

const DashboardConfigModal = ({ open, onClose, onSave, selectedCharts = [] }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(new Set(selectedCharts.map(chart => chart.id)));
  const [saving, setSaving] = useState(false);
  const allowedCategories = getDashboardEnabledCategories();

  useEffect(() => {
    if (open) {
      setSelected(new Set(selectedCharts.map(chart => chart.id)));
    }
  }, [open, selectedCharts]);

  const handleToggleChart = (chartId) => {
    const newSelected = new Set(selected);
    if (newSelected.has(chartId)) {
      newSelected.delete(chartId);
    } else {
      newSelected.add(chartId);
    }
    setSelected(newSelected);
  };

  const handleSave = async () => {
    const allCharts = [
      ...AVAILABLE_CHARTS.sales,
      ...AVAILABLE_CHARTS.branch,
      ...AVAILABLE_CHARTS.inventory,
    ].filter((chart) => allowedCategories.includes(chart.category));
    
    const selectedChartsList = allCharts
      .filter(chart => selected.has(chart.id))
      .map(chart => ({
        id: chart.id,
        name: chart.name,
        component: chart.component,
        defaultSize: chart.defaultSize,
        category: chart.category
      }));

    try {
      setSaving(true);
      await onSave(selectedChartsList);
      SnackbarUtils.success(t('dashboard_config_saved') || 'Configuración del dashboard guardada exitosamente');
      onClose();
    } catch (error) {
      console.error('Error saving dashboard config:', error);
      SnackbarUtils.error(t('error_saving_dashboard_config') || 'Error al guardar la configuración del dashboard');
    } finally {
      setSaving(false);
    }
  };

  const renderCategory = (categoryName, charts, categoryLabel) => {
    if (!allowedCategories.includes(categoryName)) return null;

    const categoryCharts = charts.filter(chart => chart.category === categoryName);
    if (categoryCharts.length === 0) return null;

    return (
      <Box key={categoryName} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {categoryLabel}
        </Typography>
        <Grid container spacing={2}>
          {categoryCharts.map((chart) => (
            <Grid item xs={12} sm={6} md={4} key={chart.id}>
              <Paper
                elevation={selected.has(chart.id) ? 3 : 1}
                sx={{
                  p: 2,
                  border: selected.has(chart.id) ? '2px solid' : '1px solid',
                  borderColor: selected.has(chart.id) ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    elevation: 4,
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => handleToggleChart(chart.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={selected.has(chart.id)}
                    onChange={() => handleToggleChart(chart.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Typography variant="body1" sx={{ flex: 1 }}>
                    {chart.name}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t('customize_dashboard') || 'Personalizar Dashboard'}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('select_charts_dashboard') || 'Selecciona los gráficos que deseas ver en tu dashboard'}
        </Typography>

        {renderCategory('sales', AVAILABLE_CHARTS.sales, t('sales') || 'Ventas')}
        {renderCategory('branch', AVAILABLE_CHARTS.branch, t('branches') || 'Sucursales')}
        {renderCategory('inventory', AVAILABLE_CHARTS.inventory, t('inventory') || 'Inventario')}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('cancel') || 'Cancelar'}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardConfigModal;
export { AVAILABLE_CHARTS };
